/**
 * CollectionPage Component
 */

import * as playerActions from '../../../redux/actions/player';
import { setHeaderType } from '../../../redux/actions/ui';
import { CollectionPageFragment } from '../../../relay/collection';
import { white } from '../../styles/colors';
import { baseline } from '../../styles/grid';
import './styles.scss';

import { RouteMatch } from 'found';
import { chain, find } from 'lodash';
import * as nextTick from 'next-tick';
import throttle from 'raf-throttle';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import * as WindowSizeListener from 'react-window-size-listener';

import Page from '../../../containers/Page';
import { getPlayerTracksFromResults } from '../../../redux/helpers/player';
import { getResponsiveAssets } from '../../../utils/image';
import { toInnerHTML } from '../../../utils/string';
import { SignUpGuardedDownloadButton } from '../../atoms/buttons/DownloadButton';
import LinkButton from '../../atoms/buttons/LinkButton';
import NProgress from '../../atoms/feedback/NProgress';
import Picture from '../../atoms/images/Picture';
import Heading from '../../atoms/text/Heading';
import { Paragraph } from '../../atoms/text/Label';
import LicenseBar from '../../molecules/blocks/LicenseBar';
import TrackList from '../../organisms/search/TrackList';
import { Size } from '../../styles/sizes';
import { DiscoverPagePath } from '../browse/Discover';
import CollectionHelmet from './helmet';

// Page Path
export const CollectionPagePath = (slug: string) => `/collection/${slug}`;

// Page Props
interface IOwnStateProps {
  currentTrackPlaying: string;
  termListIsVisible: boolean;
}

interface IOwnDispatchProps {
  initHeader(): void;
  pausePlayer(): void;
  playPlayer(): void;
  playTrackVersion(versionId: string): void;
  resetHeader(): void;
  setPlayerQueue(tracks: IPlayerTrack[]): void;
}

interface IOwnProps {
  relay: IRelayProps<ITrackSearchQueryVariables>;
  viewer: GQL.IViewer;
}

type CollectionPageProps =
  & IOwnProps
  & IOwnStateProps
  & IOwnDispatchProps
  & RouteMatch;

interface IOwnState {
  fetching: boolean;
}

const classes = new BEM('collection');

class CollectionPage extends React.Component<CollectionPageProps, IOwnState> {
  private collection: GQL.ICollection;
  private content: HTMLElement;
  private illustration: Picture;
  private illustrationNode: HTMLPictureElement;
  private tracks: GQL.ITrackSearchResult[] = [];

  private onScrollThrottled;
  private contentScrollRange: number;
  private illustrationScrollRange: number;

  constructor(props) {
    super(props);

    this.mapCollectionData();
    this.state = {
      fetching: false,
    };
  }

  componentDidMount() {
    this.props.initHeader();
    this.props.setPlayerQueue(getPlayerTracksFromResults(this.tracks));
    nextTick(() => this.initIllustrationParallax);
  }

  componentWillReceiveProps() {
    if (this.state.fetching) {
      this.setState({ fetching: false });
    }
  }

  componentDidUpdate(prevProps: CollectionPageProps, prevState: IOwnState) {
    if (prevProps.termListIsVisible !== this.props.termListIsVisible) {
      this.initIllustrationParallax();
    }

    if (prevState.fetching && !this.state.fetching) {
      this.mapCollectionData();
      this.forceUpdate();
    }
  }

  componentWillUnmount() {
    this.killIllustrationParallax();
  }

  onResize = () => {
    this.initIllustrationParallax();
  }

  onScroll = () => {
    const scrollTop = (window.scrollY / this.contentScrollRange) * this.illustrationScrollRange;
    this.illustrationNode.style.transform = `translate3d(-50%, -${scrollTop}px, 0)`;
  }

  render() {
    return !!this.tracks.length ? (
      <Page id="collection">
        <CollectionHelmet collection={this.collection} />
        <div {...classes('content-wrapper') }>
          <div {...classes('content') } ref={(node) => this.content = node!}>
            <LicenseBar {...classes('license-bar') } />
            <Heading is="h6" {...classes('category') }>{this.collection.category!.name}</Heading>
            <Heading is="h1" {...classes('name')}>
              <span dangerouslySetInnerHTML={toInnerHTML(this.collection.name)} />
            </Heading>
            {this.collection.description && (
              <Paragraph
                size="xlarge"
                text={this.collection.description}
                variant="description"
                {...classes('description') }
              />
            )}
            <div {...classes('download')}>
              <SignUpGuardedDownloadButton
                label="Download Tracks"
                track={{
                  title: `Collection: ${this.collection.name}`,
                } as any}
                assetPath={`collections/${this.collection.slug}/download`}
              />
            </div>
            <TrackList
              cardProps={{
                backgroundColor: white,
                size: 'medium',
                showListenedStatus: false,
              }}
              showSignUpCta={true}
              tracks={this.tracks}
              viewer={this.props.viewer}
              {...classes('track-listing') }
            />
            <div {...classes('discover')}>
              <LinkButton
                label="Discover More Music"
                colorScheme="dark"
                size="xlarge"
                variant="pill"
                to={DiscoverPagePath}
              />
            </div>
          </div>
          <NProgress active={this.state.fetching} style={{ top: baseline * 4 }} />
        </div>
        <div {...classes('illustration-wrapper') }>
          <div {...classes('illustration') }>
            <Picture
              ref={(node) => this.illustration = node!}
              assets={getResponsiveAssets('cover', this.collection.assets!)}
            />
          </div>
        </div>
        <WindowSizeListener onResize={this.onResize} />
      </Page>
    ) : (
      <Page id="collection" className="not-found">
        <Heading is="h2">Collection Not Found</Heading>
      </Page>
    );
  }

  private mapCollectionData() {
    if (!this.props.viewer.trackSearch!.edges!.length) {
      return;
    }

    this.tracks =
      chain(this.getNodesFromEdges(this.props))
        .thru((tracks) => {
          const collections = tracks[0].collections!.edges!.map(({ node }) => node!);
          this.collection = find(collections, { slug: this.props.routeParams.slug })!;
          return tracks;
        })
        .sortBy((track: GQL.ITrackSearchResult) => {
          return track.collections!.edges!.reduce((position, edge) => {
            return edge.node!.id === this.collection.id ?
              edge.position : position;
          }, 0);
        })
        .value();
  }

  private getNodesFromEdges(props = this.props) {
    return props.viewer.trackSearch!.edges!.map(({ node }) => node!);
  }

  private initIllustrationParallax = () => {
    this.illustrationNode = findDOMNode(this.illustration) as HTMLPictureElement;
    const playerHeight = this.props.termListIsVisible ? Size.playerWithTermListHeight : Size.playerHeight;
    const containerHeight = window.innerHeight - playerHeight;
    const { height: contentHeight } = this.content.getBoundingClientRect();
    const { height: pictureHeight } = this.illustrationNode.getBoundingClientRect();

    this.contentScrollRange = contentHeight - containerHeight;
    this.illustrationScrollRange = pictureHeight - containerHeight;
    this.onScrollThrottled = throttle(this.onScroll);
    window.addEventListener('scroll', this.onScrollThrottled);
  }

  private killIllustrationParallax() {
    window.removeEventListener('scroll', this.onScrollThrottled);
    this.onScrollThrottled = undefined;
  }
}

const ConnectedCollectionPage = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps, IStoreState>(
  ({ player, ui }) => {
    const {
      currentTrack,
      isTrackPlaying,
    } = player;

    let currentTrackPlaying = '';

    if (isTrackPlaying && currentTrack) {
      currentTrackPlaying = currentTrack.trackId;
    }

    return {
      currentTrackPlaying,
      termListIsVisible: !!ui.player && ui.player.termListIsVisible,
    };
  },
  (dispatch) => ({
    initHeader: () => {
      dispatch(setHeaderType({ type: 'simple' }));
    },
    pausePlayer: () => {
      dispatch(playerActions.pause());
    },
    playPlayer: () => {
      dispatch(playerActions.playNext());
    },
    playTrackVersion: (trackId) => {
      dispatch(playerActions.playTrackFromQueue({ trackId }));
    },
    resetHeader: () => {
      dispatch(setHeaderType({ type: 'extended' }));
    },
    setPlayerQueue: (tracks: IPlayerTrack[]) => {
      dispatch(playerActions.replaceQueue({ tracks }));
    },
  }),
)(CollectionPage);

const RelayCollectionPage = createFragmentContainer(
  ConnectedCollectionPage,
  CollectionPageFragment,
);

export default RelayCollectionPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Collection/index.tsx